/**
 * Decisions tab — drafter-time design rationale.
 *
 * Each row pairs a one-line decision label (PERSON-spine vs COHORT, time
 * bounds, NULL semantics, value-selection tie-breaks, anti-patterns
 * avoided) with the rationale behind it. Sourced from
 * ``manifest.design_decisions`` written by the av_drafter on
 * ``draft_de_novo_analytical_variable``. The OR-5964 third surface — durable record of
 * *why* the SQL is shaped the way it is, attached to the artifact so it
 * outlives the chat scrollback.
 *
 * Tab is hidden when the manifest carried no rows; see
 * ``tabsForArtifact``.
 */

import { Box, Stack, Typography } from '@mui/material';

import type { EpiArtifact } from '../../types/epi-chat-types';

export const DecisionsTab = ({ artifact }: { artifact: EpiArtifact }) => {
  const decisions = artifact.designDecisions ?? [];
  if (decisions.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No design decisions recorded for this variable. The agent will fill this when the SQL
          involves a non-trivial choice (spine table, time bounds, NULL semantics, etc).
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ padding: 2 }} data-testid="artifact-decisions-tab">
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.25 }}>
        Why this variable is shaped the way it is. Each row pairs a design choice with the rationale
        behind it — the durable audit trail for trade-offs the agent committed to while drafting.
      </Typography>
      <Stack>
        {decisions.map((row, idx) => (
          <Stack
            // Decisions are rendered in author order; the index is stable enough
            // for a key since the manifest list is immutable per artifact version.
            key={`${row.decision}-${idx}`}
            spacing={0.25}
            sx={{
              paddingY: 1.25,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              '&:first-of-type': { borderTop: 0, paddingTop: 0 },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.decision}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {row.rationale}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
